/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useSearch } from "@packages/framework";
import { useSession } from "@packages/sdk";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { useSearchParamState } from "../../../hooks/useSearchParamState";
import { Loader } from "../other/Loader/Loader.emotion";
import type { AuthMainFlowProps } from "./AuthMainFlow.emotion";
import { AuthMainFlow } from "./AuthMainFlow.emotion";
import { AuthNamesFlow } from "./AuthNamesFlow.emotion";
import type {
  AuthFlow,
  AuthFlowProps,
  AuthMethod,
  AuthProps,
} from "./AuthTypes.emotion";
import { EmailAuthFlow } from "./EmailAuthFlow.emotion";
import { OtpAuthFlow } from "./OtpAuthFlow.emotion";
import { PasswordAuthFlow } from "./PasswordAuthFlow.emotion";
import { PasswordAuthFlowWithSignup } from "./PasswordAuthFlowWithSignup.emotion";
import { PhoneAuthFlow } from "./PhoneAuthFlow.emotion";

type AuthComponentProps = AuthProps & {
  onFlowChange?: (flow: AuthFlow) => void;
  layoutCSS?: Interpolation;
};

export const Auth = ({
  authMainFlowProps = {},
  onFlowChange: handleFlowChange,
  allowedMethods = ["email", "phone", "apple", "google"],
  hideHeader,
  onAuth,
  ...props
}: AuthComponentProps & { authMainFlowProps?: Partial<AuthMainFlowProps> }) => {
  const initialFlow = props.email ? "email" : props.phone ? "phone" : "main";
  const [flow, setFlow] = useSearchParamState<false, AuthFlow>({
    defaultValue: initialFlow,
    key: "flow",
    removeFromUrlIfDefaultValue: false,
    multiple: false,
  });
  const [state, setState] = useState<Record<string, any>>({});
  const [initialCheck, setInitialCheck] = useState<boolean>(false);
  const { status } = useSession();
  const search = useSearch();
  const [method, setMethod] = useState<AuthMethod>("email");
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useSession({
    onAuthenticated: ({ isNewUser }) => {
      setIsLoading(true);
      if (flow !== "names" && isNewUser) setFlow("names");
      else onAuth({ isNewUser });
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (
      !initialCheck &&
      !search.has("isAuthenticated") &&
      !search.has("isNewUser")
    ) {
      if (status === "authenticated") onAuth?.({ isNewUser: false });
      if (status !== "loading") setInitialCheck(true);
    }
  }, [status, initialCheck]);

  useEffect(() => {
    const urlFlow = search.get("flow");
    if (
      urlFlow !== flow &&
      ["email", "phone", "phone_code", "password", "main"].includes(urlFlow)
    ) {
      setFlow(urlFlow as AuthFlow);
      handleFlowChange?.(urlFlow as AuthFlow);
    }
  }, [search]);

  /** setSessionListenerURLParams onAuth for forms  */
  const handleAuth: AuthFlowProps["onAuth"] = ({ data }) => {
    login({ isNewUser: data?.is_new, token: data?.oauth?.access_token });
  };

  const onFlowChange = (flow: AuthFlow) => {
    setFlow(flow);
    handleFlowChange?.(flow);
  };

  const flowProps = {
    ...props,
    onAuth: handleAuth,
    hideHeader,
    allowedMethods,
    flow,
    setFlow: onFlowChange,
    state,
    setState,
    method,
    setMethod,
  };

  const flows: Record<AuthFlow, ReactNode> = {
    main: <AuthMainFlow {...flowProps} {...authMainFlowProps} />,
    email: <EmailAuthFlow {...flowProps} />,
    password: <PasswordAuthFlow {...flowProps} />,
    /** Allow Password signups IF NOT in current A/B magic-link treatment */
    passwordWithSignup: <PasswordAuthFlowWithSignup {...flowProps} />,
    phone: <PhoneAuthFlow {...flowProps} />,
    otp: <OtpAuthFlow {...flowProps} />,
    /** Use onAuth for "names" */
    names: <AuthNamesFlow {...{ ...flowProps, onAuth }} />,
  };

  if (isLoading) return <Loader />;

  return flows[flow];
};

Auth.displayName = "Auth";
